import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import HotRankList from "@/components/feature/HotRankList";
import FilterBar, { type FilterState } from "./components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  filterVideos,
  getCategoryById,
  getHotVideos,
  getPopularInCategory,
  paginate,
} from "@/lib/videos";

const defaultFilters: FilterState = {
  timeRange: "全部时间",
  level: "全部等级",
  region: "全部地区",
};

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  usePageMeta(
    `${category ? category.name : "赛事分类"}视频 - 51视频每日大赛今日黑榜合集`,
    category
      ? `${category.description}，按赛事时间段、比赛等级与地区筛选，支持按时上传时间与热度排序。`
      : "赛事分类页面不存在，请返回首页浏览每日赛事黑榜视频合集。",
  );

  if (!category) {
    return (
      <PageShell>
        <div className="container-page py-20 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-error-warning-line" />
          </span>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950">没有找到这个赛事分类</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-foreground-600">
            该分类可能已经调整。你可以返回首页，或从分类快捷入口重新选择感兴趣的赛事类型。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="btn-primary">返回首页</Link>
            <Link to="/search" className="btn-ghost">浏览全部视频</Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const filtered = filterVideos({
    categoryId: category.id,
    timeRange: filters.timeRange,
    level: filters.level,
    region: filters.region,
    sort,
  });
  const paged = paginate(filtered, page, 4);

  const handleFilterChange = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const handleSortChange = (next: "latest" | "hot") => {
    setSort(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setSort("latest");
    setPage(1);
  };

  const handlePageChange = (next: number) => {
    setPage(next);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell>
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "赛事分类" }, { label: category.name }]} />
          <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950">
                  <i className={`${category.icon} text-xl`} />
                </span>
                <div>
                  <h1 className="text-2xl font-bold text-foreground-950 md:text-[30px]">{category.name}视频</h1>
                  <p className="mt-1 font-label text-[11px] tracking-[0.24em] text-secondary-600">
                    {filtered.length} 条收录 · 每日更新
                  </p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{category.intro}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/search" className="btn-ghost h-9 px-4 text-xs">
                <i className="ri-search-line" />
                搜索全站
              </Link>
              <Link to="/" className="btn-primary h-9 px-4 text-xs">
                <i className="ri-home-4-line" />
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="container-page scroll-mt-20 py-8 md:py-10" ref={listRef}>
        <FilterBar
          value={filters}
          sort={sort}
          resultCount={filtered.length}
          onChange={handleFilterChange}
          onSortChange={handleSortChange}
          onReset={handleReset}
        />

        <div className="mt-7 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-9">
          <div>
            {paged.items.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2">
                  {paged.items.map((video, index) => (
                    <div key={video.id} className="anim-fade-up" style={{ animationDelay: `${index * 45}ms` }}>
                      <VideoCard video={video} />
                    </div>
                  ))}
                </div>
                <Pagination page={paged.page} totalPages={paged.totalPages} onChange={handlePageChange} />
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-inbox-line" />
                </span>
                <h2 className="mt-4 text-lg font-semibold text-foreground-950">没有匹配的黑榜视频</h2>
                <p className="mx-auto mt-2 max-w-sm text-sm text-foreground-600">
                  当前筛选条件组合下暂无内容，试着放宽时间段或比赛等级，也可以直接重置全部条件。
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <button type="button" onClick={handleReset} className="btn-primary h-10 px-5">
                    重置筛选
                  </button>
                  <Link to="/search" className="btn-ghost h-10 px-5">搜索其他关键词</Link>
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <div className="panel p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-lg text-accent-600" />
                本站热榜
              </h2>
              <div className="mt-3">
                <HotRankList videos={getHotVideos(5)} />
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <h2 className="text-sm font-bold text-foreground-950">同分类热门</h2>
              <div className="mt-3 space-y-2">
                {getPopularInCategory(category.id, 4).map((video) => (
                  <Link
                    key={video.id}
                    to={`/detail/${video.id}`}
                    className="flex items-start gap-2 text-xs leading-relaxed text-foreground-600 transition-colors hover:text-primary-700"
                  >
                    <i className="ri-play-circle-line mt-0.5 text-sm text-primary-500" />
                    <span className="line-clamp-2-custom">{video.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-primary-100 bg-primary-50 p-4 md:p-5">
              <h2 className="text-sm font-bold text-primary-800">赛事时间段说明</h2>
              <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-primary-700">
                <li>· 本周：最近 7 天内上传的黑榜片段</li>
                <li>· 本月：最近 30 天内上传的条目</li>
                <li>· 经典：跨赛季的存档回顾内容</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}